import React, { useState, useEffect, useMemo } from "react";
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis,
  Tooltip as RechartsTip,
  ResponsiveContainer, Legend,
} from "recharts";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { getGeopolitical, generateGeopoliticalRead } from "../services/api";
import { PageAlert } from "../components/atoms";
import { ALIGN_COLOR, ALIGN_BG, ALIGN_LABEL, REGIONS, REGION_SHORT } from "../config/geopolitics";
import AiSignals from "../components/AiSignals";
import { INSIGHTS_AI_READS_ENABLED } from "../config/featureFlags";
import { Info, AlertTriangle, Download, X } from "lucide-react";

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";

// Colour per "Geopolitical read" signal type.
const GEO_COLOR = { opportunity: "#15803d", risk: "#C1121F", balance: "#F18F18", coverage: "#960659" };

// World atlas TopoJSON — loaded from jsDelivr; no external runtime dependency required.
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Natural Earth name → canonical name used in show_countries.country
// Only entries where the names differ; all other Natural Earth names are matched directly.
const GEO_ALIAS = {
  "United States of America":           "United States",
  "Russian Federation":                 "Russia",
  "Viet Nam":                           "Vietnam",
  "Korea":                              "South Korea",
  "Iran (Islamic Republic of)":         "Iran",
  "Syrian Arab Republic":               "Syria",
  "Lao PDR":                            "Laos",
  "Dem. Rep. Congo":                    "DR Congo",
  "Dem. Rep. Korea":                    null,
  "Macedonia":                          "North Macedonia",
  "Bolivia (Plurinational State of)":   "Bolivia",
  "Venezuela (Bolivarian Republic of)": "Venezuela",
  "United Republic of Tanzania":        "Tanzania",
};

// ── Colour helpers ─────────────────────────────────────────────────────────────
function alignFill(flag, count, maxCount) {
  if (!count) return "#F4F4F5";
  const r = count / Math.max(maxCount, 1);
  if (flag === "friendly")   return r > 0.4 ? "#15803d" : r > 0.15 ? "#22c55e" : "#86efac";
  if (flag === "unfriendly") return r > 0.4 ? "#A00F1A" : r > 0.15 ? "#C1121F" : "#E39B98";
  return "#D8DADC";
}

function countFill(count, maxCount) {
  if (!count) return "#FAFAFA";
  const r = count / Math.max(maxCount, 1);
  if (r > 0.7) return "#26262A";
  if (r > 0.4) return "#E8431A";
  if (r > 0.2) return "#E8431A";
  if (r > 0.05) return "#F3A78C";
  return "#FCEBE5";
}

// ── CSV export (no extra library needed) ──────────────────────────────────────
function exportCSV(rows) {
  const hdr  = ["#", "Country", "ISO3", "Region", "Alignment (Russian Govt designation)", "Exhibitor Count"];
  const body = rows.map((r, i) => [
    i + 1, `"${r.country}"`, r.country_iso3 || "",
    `"${r.region}"`, ALIGN_LABEL[r.alignment_flag] || r.alignment_flag,
    r.exhibitor_count,
  ].join(","));
  const blob = new Blob([[hdr.join(","), ...body].join("\n")], { type: "text/csv" });
  const a = Object.assign(document.createElement("a"), {
    href: URL.createObjectURL(blob),
    download: "country_participation.csv",
  });
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── AlignmentCard ─────────────────────────────────────────────────────────────
function AlignmentCard({ data }) {
  const buckets = { friendly: 0, unfriendly: 0, unknown: 0 };
  data.forEach(r => { buckets[r.alignment_flag] = (buckets[r.alignment_flag] || 0) + r.exhibitor_count; });
  const total   = Object.values(buckets).reduce((a, b) => a + b, 0);
  const pieData = Object.entries(buckets)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ k, name: ALIGN_LABEL[k], value: v }));

  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"16px 18px" }}>
      <div style={{ fontSize:12, fontWeight:700, color:"#26262A", marginBottom:12 }}>Alignment Summary</div>
      {total === 0 ? (
        <div style={{ textAlign:"center", padding:"32px 0", color:"#818084", fontSize:11 }}>No data</div>
      ) : (
        <>
          <div style={{ display:"flex", justifyContent:"center" }}>
            <PieChart width={160} height={160}>
              <Pie data={pieData} cx={80} cy={80} innerRadius={50} outerRadius={74} dataKey="value" paddingAngle={3}>
                {pieData.map((e, i) => <Cell key={i} fill={ALIGN_COLOR[e.k]} />)}
              </Pie>
              <RechartsTip formatter={v => [v.toLocaleString(), ""]} contentStyle={{ fontSize:10, fontFamily:F }} />
            </PieChart>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:4 }}>
            {pieData.map(e => (
              <div key={e.k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:11 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:ALIGN_COLOR[e.k], flexShrink:0 }} />
                  <span style={{ color:"#3F3F46", fontWeight:500 }}>{e.name}</span>
                </div>
                <div style={{ display:"flex", gap:8, color:"#818084" }}>
                  <span style={{ fontWeight:700, fontFamily:M }}>{e.value.toLocaleString()}</span>
                  <span style={{ minWidth:28, textAlign:"right" }}>{total ? `${((e.value / total) * 100).toFixed(0)}%` : ""}</span>
                </div>
              </div>
            ))}
            <div style={{ borderTop:"1px solid #F4F4F5", paddingTop:6, display:"flex", justifyContent:"space-between", fontSize:11, fontWeight:700, color:"#26262A" }}>
              <span>Total</span>
              <span style={{ fontFamily:M }}>{total.toLocaleString()}</span>
            </div>
          </div>
        </>
      )}
      <div style={{ marginTop:10, fontSize:9, color:"#818084", lineHeight:1.5 }}>
        Per Russian government designation (Order 83-r, 2022). Factual — not editorial.
      </div>
    </div>
  );
}

// ── CountryBar ────────────────────────────────────────────────────────────────
function CountryBar({ data, selectedCountry, setSelectedCountry, colorByAlignment }) {
  const top      = data.slice(0, 25);
  const maxCount = Math.max(...top.map(r => r.exhibitor_count), 1);

  const barTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background:"#fff", border:"1px solid #D8DADC", borderRadius:6, padding:"8px 10px", fontSize:10, fontFamily:F }}>
        <div style={{ fontWeight:700, marginBottom:2 }}>{d.country}</div>
        <div>{d.exhibitor_count.toLocaleString()} exhibitors</div>
        <div style={{ color:ALIGN_COLOR[d.alignment_flag], fontWeight:600 }}>{ALIGN_LABEL[d.alignment_flag]}</div>
        <div style={{ color:"#818084" }}>{d.region}</div>
      </div>
    );
  };

  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"16px 18px" }}>
      <div style={{ fontSize:12, fontWeight:700, color:"#26262A", marginBottom:8 }}>
        Country Ranking (top {top.length})
        {selectedCountry && (
          <span style={{ fontWeight:400, color:"#818084", fontSize:10 }}> — click bar to deselect</span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={Math.max(200, top.length * 24)}>
        <BarChart data={top} layout="vertical" margin={{ left:4, right:24, top:0, bottom:0 }} barCategoryGap="20%">
          <XAxis type="number" tick={{ fontSize:9, fontFamily:F }} stroke="#D8DADC"
            tickFormatter={v => v >= 1000 ? `${v / 1000}K` : v} />
          <YAxis type="category" dataKey="country" width={130} tick={{ fontSize:10, fontFamily:F }} />
          <RechartsTip content={barTooltip} />
          <Bar
            dataKey="exhibitor_count"
            radius={[0, 3, 3, 0]}
            style={{ cursor:"pointer" }}
            onClick={entry => setSelectedCountry(entry.country === selectedCountry ? null : entry.country)}
          >
            {top.map((entry, i) => (
              <Cell
                key={`c-${i}`}
                fill={colorByAlignment
                  ? alignFill(entry.alignment_flag, entry.exhibitor_count, maxCount)
                  : countFill(entry.exhibitor_count, maxCount)}
                opacity={selectedCountry && entry.country !== selectedCountry ? 0.25 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── WorldMap ──────────────────────────────────────────────────────────────────
function WorldMap({ data, selectedCountry, setSelectedCountry, colorByAlignment }) {
  const [tooltip, setTooltip] = useState(null);

  const maxCount = useMemo(() => Math.max(...data.map(r => r.exhibitor_count), 1), [data]);

  const lookup = useMemo(() => {
    const m = {};
    data.forEach(r => { m[r.country] = r; });
    return m;
  }, [data]);

  const getRow  = name => lookup[GEO_ALIAS.hasOwnProperty(name) ? GEO_ALIAS[name] : name] ?? null;
  const getFill = name => {
    const row = getRow(name);
    if (!row) return "#F4F4F5";
    if (selectedCountry && row.country !== selectedCountry) return "#D8DADC";
    return colorByAlignment
      ? alignFill(row.alignment_flag, row.exhibitor_count, maxCount)
      : countFill(row.exhibitor_count, maxCount);
  };

  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"16px 18px", position:"relative" }}>
      <div style={{ fontSize:12, fontWeight:700, color:"#26262A", marginBottom:2 }}>World Participation Map</div>
      <div style={{ fontSize:9, color:"#818084", marginBottom:4 }}>Click a country to cross-filter all panels</div>
      <ComposableMap projectionConfig={{ scale:145 }} style={{ width:"100%", height:"auto" }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) => geographies.map(geo => {
            const row = getRow(geo.properties.name);
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill={getFill(geo.properties.name)}
                stroke="#ffffff"
                strokeWidth={0.4}
                style={{
                  default: { outline:"none" },
                  hover:   { outline:"none", cursor: row ? "pointer" : "default", opacity: row ? 0.8 : 1 },
                  pressed: { outline:"none" },
                }}
                onClick={() => row && setSelectedCountry(row.country === selectedCountry ? null : row.country)}
                onMouseEnter={e => row && setTooltip({
                  x: e.clientX, y: e.clientY,
                  label: `${row.country} · ${row.exhibitor_count.toLocaleString()} exhibitors`,
                  sub: `${ALIGN_LABEL[row.alignment_flag]} · ${row.region}`,
                  color: ALIGN_COLOR[row.alignment_flag],
                })}
                onMouseMove={e => setTooltip(p => p ? { ...p, x: e.clientX, y: e.clientY } : null)}
                onMouseLeave={() => setTooltip(null)}
              />
            );
          })}
        </Geographies>
      </ComposableMap>

      {/* Map legend */}
      <div style={{ display:"flex", gap:12, marginTop:4 }}>
        {colorByAlignment
          ? [["Friendly","#22c55e"],["Unfriendly","#C1121F"],["No data","#F4F4F5"]].map(([label, color]) => (
              <div key={label} style={{ display:"flex", alignItems:"center", gap:4, fontSize:9, color:"#818084" }}>
                <div style={{ width:12, height:8, background:color, borderRadius:2, border:"1px solid #D8DADC" }} />
                {label}
              </div>
            ))
          : [["Low","#FCEBE5"],["","#E8431A"],["High","#26262A"]].map(([label, color]) => (
              <div key={color} style={{ display:"flex", alignItems:"center", gap:4, fontSize:9, color:"#818084" }}>
                <div style={{ width:14, height:8, background:color, borderRadius:2 }} />
                {label}
              </div>
            ))
        }
      </div>

      {/* Floating tooltip */}
      {tooltip && (
        <div style={{
          position:"fixed", left:tooltip.x + 12, top:tooltip.y - 44, zIndex:9999,
          background:"#26262A", color:"#fff", borderRadius:7, padding:"8px 12px",
          fontSize:11, fontFamily:F, pointerEvents:"none",
          boxShadow:"0 4px 14px rgba(0,0,0,0.35)", maxWidth:280,
        }}>
          <div style={{ fontWeight:700 }}>{tooltip.label}</div>
          <div style={{ color:tooltip.color, fontSize:9, marginTop:2, fontWeight:600 }}>{tooltip.sub}</div>
        </div>
      )}
    </div>
  );
}

// ── RegionRollup ──────────────────────────────────────────────────────────────
function RegionRollup({ data }) {
  const regionData = REGIONS.map(r => ({
    region:     REGION_SHORT[r] || r,
    friendly:   data.filter(d => d.region === r && d.alignment_flag === "friendly").reduce((s, x) => s + x.exhibitor_count, 0),
    unfriendly: data.filter(d => d.region === r && d.alignment_flag === "unfriendly").reduce((s, x) => s + x.exhibitor_count, 0),
    unknown:    data.filter(d => d.region === r && d.alignment_flag === "unknown").reduce((s, x) => s + x.exhibitor_count, 0),
  })).filter(r => r.friendly + r.unfriendly + r.unknown > 0);

  const showUnknown = regionData.some(r => r.unknown > 0);

  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"16px 18px" }}>
      <div style={{ fontSize:12, fontWeight:700, color:"#26262A", marginBottom:12 }}>By Region</div>
      {regionData.length === 0 ? (
        <div style={{ textAlign:"center", padding:"32px 0", color:"#818084", fontSize:11 }}>No data</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={regionData} barCategoryGap="25%">
            <XAxis dataKey="region" tick={{ fontSize:8, fontFamily:F }} stroke="#D8DADC" />
            <YAxis tick={{ fontSize:9, fontFamily:F }} stroke="#D8DADC"
              tickFormatter={v => v >= 1000 ? `${v / 1000}K` : v} />
            <RechartsTip contentStyle={{ fontSize:10, fontFamily:F }} />
            <Legend iconSize={8} wrapperStyle={{ fontSize:9, fontFamily:F }} />
            <Bar dataKey="friendly"   name="Friendly"   fill={ALIGN_COLOR.friendly}   radius={[3,3,0,0]} />
            <Bar dataKey="unfriendly" name="Unfriendly" fill={ALIGN_COLOR.unfriendly} radius={[3,3,0,0]} />
            {showUnknown && <Bar dataKey="unknown" name="Unknown" fill={ALIGN_COLOR.unknown} radius={[3,3,0,0]} />}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── GeoDetailTable ────────────────────────────────────────────────────────────
function GeoDetailTable({ data, selectedCountry, setSelectedCountry }) {
  const [sortCol, setSortCol] = useState("exhibitor_count");
  const [sortAsc, setSortAsc] = useState(false);

  const rows   = selectedCountry ? data.filter(r => r.country === selectedCountry) : data;
  const sorted = [...rows].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    if (typeof av === "number") return sortAsc ? av - bv : bv - av;
    return sortAsc
      ? String(av ?? "").localeCompare(String(bv ?? ""))
      : String(bv ?? "").localeCompare(String(av ?? ""));
  });

  const thStyle = (active) => ({
    textAlign:"left", padding:"8px 10px", fontSize:9, fontWeight:700,
    textTransform:"uppercase", letterSpacing:0.4, borderBottom:"2px solid #D8DADC",
    background:"#FAFAFA", cursor:"pointer", whiteSpace:"nowrap", userSelect:"none",
    color: active ? "#E8431A" : "#818084",
  });
  const th = (col, label) => (
    <th key={col} style={thStyle(sortCol === col)}
      onClick={() => { setSortAsc(sortCol === col ? !sortAsc : false); setSortCol(col); }}>
      {label}{sortCol === col ? (sortAsc ? " ▲" : " ▼") : ""}
    </th>
  );

  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"16px 18px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <span style={{ fontSize:12, fontWeight:700, color:"#26262A" }}>
          Country Detail ({sorted.length} {sorted.length === 1 ? "country" : "countries"})
        </span>
        <button onClick={() => exportCSV(sorted)} style={{
          display:"flex", alignItems:"center", gap:5,
          padding:"5px 10px", borderRadius:6, border:"1px solid #D8DADC",
          background:"#fff", fontSize:10, fontWeight:600, color:"#3F3F46",
          cursor:"pointer", fontFamily:F,
        }}>
          <Download size={11} /> Export CSV
        </button>
      </div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
          <thead>
            <tr>
              {th("_rank","#")}
              {th("country","Country")}
              {th("country_iso3","ISO3")}
              {th("region","Region")}
              {th("alignment_flag","Alignment")}
              {th("exhibitor_count","Exhibitors")}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const isSelected = r.country === selectedCountry;
              return (
                <tr key={r.country}
                  onClick={() => setSelectedCountry(isSelected ? null : r.country)}
                  style={{ cursor:"pointer", background: isSelected ? "#FCEBE5" : i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td style={{ padding:"8px 10px", color:"#818084", fontWeight:600, fontFamily:M }}>{i + 1}</td>
                  <td style={{ padding:"8px 10px", fontWeight:600 }}>{r.country}</td>
                  <td style={{ padding:"8px 10px", color:"#818084", fontFamily:M }}>{r.country_iso3 || "—"}</td>
                  <td style={{ padding:"8px 10px", color:"#818084" }}>{r.region}</td>
                  <td style={{ padding:"8px 10px" }}>
                    <span style={{
                      display:"inline-block", padding:"2px 7px", borderRadius:4, fontSize:10, fontWeight:600,
                      color: ALIGN_COLOR[r.alignment_flag] || "#818084",
                      background: ALIGN_BG[r.alignment_flag]  || "#F4F4F5",
                    }}>
                      {ALIGN_LABEL[r.alignment_flag] || r.alignment_flag}
                    </span>
                  </td>
                  <td style={{ padding:"8px 10px", fontWeight:700, fontFamily:M }}>
                    {r.exhibitor_count.toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── InsightsGeoSection (default export) ───────────────────────────────────────
export default function InsightsGeoSection({ filterEvent, filterYear, filterShow }) {
  const [data,             setData]             = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [selectedCountry,  setSelectedCountry]  = useState(null);
  const [colorByAlignment, setColorByAlignment] = useState(true);

  // "Geopolitical read" — on-demand AI alignment analysis (grounded in the numbers)
  const [geoRead,    setGeoRead]    = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError,   setGeoError]   = useState(null);

  useEffect(() => {
    if (!filterEvent) { setData([]); setSelectedCountry(null); return; }
    setLoading(true);
    setSelectedCountry(null);
    const p = { ite_event_id: filterEvent };
    if (filterYear) p.year    = Number(filterYear);
    if (filterShow) p.show_id = filterShow;
    getGeopolitical(p)
      .then(d  => setData(Array.isArray(d) ? d : []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [filterEvent, filterYear, filterShow]);

  // Clear any generated read when the filter scope changes (avoid stale AI).
  useEffect(() => { setGeoRead(null); setGeoError(null); }, [filterEvent, filterYear, filterShow]);

  // Generate the "Geopolitical read" from the current alignment numbers.
  const runGeoRead = () => {
    setGeoLoading(true);
    setGeoError(null);
    const b = { friendly: 0, unfriendly: 0, unknown: 0 };
    data.forEach(r => { b[r.alignment_flag] = (b[r.alignment_flag] || 0) + (r.exhibitor_count || 0); });
    const top = [...data]
      .sort((a, c) => (c.exhibitor_count || 0) - (a.exhibitor_count || 0))
      .slice(0, 8)
      .map(r => ({ name: r.country, count: r.exhibitor_count || 0, alignment: ALIGN_LABEL[r.alignment_flag] || r.alignment_flag, region: r.region || "" }));
    generateGeopoliticalRead({
      scope: filterEvent || "all events",
      year: filterYear || "all",
      total_exhibitors: data.reduce((s, r) => s + (r.exhibitor_count || 0), 0),
      friendly: b.friendly, unfriendly: b.unfriendly, unknown: b.unknown,
      top_countries: top,
    })
      .then(res => {
        if (res?.error) {
          setGeoError(String(res.error).includes("not configured")
            ? "AI isn't configured on this environment."
            : "Couldn't generate — please retry.");
          setGeoRead(null);
        } else {
          setGeoRead(Array.isArray(res?.signals) ? res.signals : []);
        }
      })
      .catch(() => setGeoError("Couldn't reach the AI service."))
      .finally(() => setGeoLoading(false));
  };

  if (!filterEvent) {
    return (
      <PageAlert
        type="info"
        icon={Info}
        title="Select an ITE Event to view geopolitical participation"
        message="Country participation and alignment data will appear once you choose an ITE event from the filter above."
      />
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign:"center", padding:"48px 0", color:"#818084", fontSize:12, fontFamily:F }}>
        Loading geopolitical data…
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <PageAlert
        type="warning"
        icon={AlertTriangle}
        title="No geopolitical data for the selected filters"
        message="No country participation records found for this combination. Try a different ITE event, year, or competitor show."
      />
    );
  }

  return (
    <div style={{ fontFamily:F }}>

      {/* Section header + controls */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:800, color:"#26262A" }}>
            Country Participation &amp; Geopolitical Alignment
          </div>
          <div style={{ fontSize:9, color:"#818084", marginTop:2 }}>
            Alignment per Russian government designation (Order 83-r, 2022) — factual classification, not editorial.
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {selectedCountry && (
            <button onClick={() => setSelectedCountry(null)} style={{
              display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:6,
              border:"1px solid #E39B98", background:"#fdecec",
              color:"#C1121F", fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:F,
            }}>
              <X size={10} /> {selectedCountry}
            </button>
          )}
          <button onClick={() => setColorByAlignment(v => !v)} style={{
            padding:"5px 12px", borderRadius:6, border:"1px solid #D8DADC",
            background: colorByAlignment ? "#FCEBE5" : "#fff",
            color:      colorByAlignment ? "#E8431A" : "#3F3F46",
            fontWeight: colorByAlignment ? 700 : 400,
            fontSize:10, cursor:"pointer", fontFamily:F,
          }}>
            {colorByAlignment ? "Coloring: alignment" : "Coloring: count"}
          </button>
        </div>
      </div>

      {/* Geopolitical read — on-demand AI alignment analysis (grounded in the numbers) */}
      {INSIGHTS_AI_READS_ENABLED && (
        <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"14px 18px", marginBottom:14 }}>
          <AiSignals
            title="Geopolitical read"
            signals={geoRead}
            loading={geoLoading}
            error={geoError}
            onGenerate={runGeoRead}
            colorByType={GEO_COLOR}
            emptyText="AI recommendations from the alignment split & top countries — click Generate."
          />
        </div>
      )}

      {/* Row 1: Alignment summary + Country ranking bar */}
      <div style={{ display:"grid", gridTemplateColumns:"240px 1fr", gap:14, marginBottom:14 }}>
        <AlignmentCard data={data} />
        <CountryBar
          data={data}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
          colorByAlignment={colorByAlignment}
        />
      </div>

      {/* Row 2: World map + Region rollup */}
      <div style={{ display:"grid", gridTemplateColumns:"3fr 2fr", gap:14, marginBottom:14 }}>
        <WorldMap
          data={data}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
          colorByAlignment={colorByAlignment}
        />
        <RegionRollup data={data} />
      </div>

      {/* Row 3: Detail table */}
      <GeoDetailTable
        data={data}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
      />
    </div>
  );
}
